import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from '../App'
import { getPrograms, startSession, completeSession, getAICoachTip } from '../lib/supabase'

const SECTION_COLORS = {
  tech:  { color: 'var(--blue)',   bg: 'var(--blue-dim)',   icon: '🎹' },
  comp:  { color: 'var(--purple)', bg: 'var(--purple-dim)', icon: '🎼' },
  impro: { color: 'var(--amber)',  bg: 'var(--amber-dim)',  icon: '🎵' },
  rep:   { color: 'var(--green)',  bg: 'var(--green-dim)',  icon: '🎶' },
}
const getColor = (id) => SECTION_COLORS[id] || SECTION_COLORS.tech

const fmt = (secs) => `${String(Math.floor(secs / 60)).padStart(2,'0')}:${String(secs % 60).padStart(2,'0')}`

// ── PROGRAM SELECTOR ──────────────────────────────
function ProgramSelector({ onStart }) {
  const { user } = useAuth()
  const [programs, setPrograms] = useState([])
  const [selected, setSelected] = useState(null)
  const [timerMode, setTimerMode] = useState(user?.timer_mode || 'simple')

  useEffect(() => {
    getPrograms(user?.instrument || 'all').then(p => { setPrograms(p); setSelected(p[0]) })
  }, [user])

  const timerModes = [
    { key: 'simple',    icon: '⏱', label: 'Απλό timer', sub: 'Honor system — εσύ αποφασίζεις' },
    { key: 'mic',       icon: '🎤', label: 'Timer + μικρόφωνο', sub: 'Παγώνει χωρίς ανίχνευση ήχου' },
    { key: 'headphones',icon: '🎧', label: 'Με ακουστικά', sub: 'Timer χωρίς ανίχνευση — we play music!' },
  ]

  return (
    <div style={{ padding: '20px 16px' }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>Ώρα να</div>
        <div className="page-title">παίξουμε 🎵</div>
      </div>

      <div className="section-label">Επίλεξε πρόγραμμα</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
        {programs.map(p => (
          <div key={p.id} onClick={() => setSelected(p)}
            style={{
              background: selected?.id === p.id ? 'var(--blue-dim)' : 'var(--navy-light)',
              border: `1px solid ${selected?.id === p.id ? 'rgba(59,125,255,0.4)' : 'var(--border)'}`,
              borderRadius: 'var(--radius)', padding: '14px 16px', cursor: 'pointer',
              transition: 'all 0.15s',
            }}>
            <div style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>{p.title}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.description}</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              {(p.sections || []).map(s => (
                <span key={s.id} className="tag tag-blue" style={{ fontSize: 11 }}>{s.icon} {s.name} {s.duration_minutes}λ</span>
              ))}
            </div>
          </div>
        ))}
        <div onClick={() => onStart(null, 'free', timerMode)}
          style={{ background: 'var(--navy-light)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px', cursor: 'pointer' }}>
          <div style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>✍️ Free log</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Καταγράφεις ελεύθερα τι έκανες</div>
        </div>
      </div>

      <div className="section-label">Τρόπος παρακολούθησης</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
        {timerModes.map(m => (
          <div key={m.key} onClick={() => setTimerMode(m.key)}
            style={{
              background: timerMode === m.key ? 'var(--blue-dim)' : 'var(--navy-light)',
              border: `1px solid ${timerMode === m.key ? 'rgba(59,125,255,0.4)' : 'var(--border)'}`,
              borderRadius: 'var(--radius-sm)', padding: '12px 14px',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12,
            }}>
            <span style={{ fontSize: 22 }}>{m.icon}</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{m.label}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{m.sub}</div>
            </div>
            {timerMode === m.key && <span style={{ marginLeft: 'auto', color: 'var(--blue)', fontSize: 16 }}>✓</span>}
          </div>
        ))}
      </div>

      <button className="btn-primary" onClick={() => selected && onStart(selected, 'guided', timerMode)} disabled={!selected}>
        ▶ Ξεκίνα session
      </button>
    </div>
  )
}

// ── ACTIVE SESSION TIMER ──────────────────────────
function ActiveSession({ program, timerMode, sessionId, onComplete }) {
  const sections = program?.sections || []
  const [sectionIdx, setSectionIdx] = useState(0)
  const [secsLeft, setSecsLeft] = useState((sections[0]?.duration_minutes || 8) * 60)
  const [running, setRunning] = useState(true)
  const [aiTip, setAiTip] = useState('')
  const [micActive, setMicActive] = useState(false)
  const [log, setLog] = useState(sections.map(s => ({ section_id: s.id, minutes: 0, note: '' })))
  const [notes, setNotes] = useState('')
  const [showComplete, setShowComplete] = useState(false)
  const intervalRef = useRef(null)
  const startTimeRef = useRef(Date.now())
  const micRef = useRef(null)
  const { user } = useAuth()

  const currentSection = sections[sectionIdx]
  const totalSecs = (currentSection?.duration_minutes || 8) * 60

  // Load AI tip on section change
  useEffect(() => {
    if (!currentSection) return
    setAiTip('')
    getAICoachTip(currentSection, user?.instrument || 'piano', user?.level || 'beginner', program?.title || '')
      .then(tip => tip && setAiTip(tip))
  }, [sectionIdx])

  // Mic detection
  useEffect(() => {
    if (timerMode !== 'mic') return
    navigator.mediaDevices?.getUserMedia({ audio: true }).then(stream => {
      const ctx = new AudioContext()
      const analyser = ctx.createAnalyser()
      const src = ctx.createMediaStreamSource(stream)
      src.connect(analyser)
      micRef.current = { stream, ctx, analyser }
      const check = () => {
        const data = new Uint8Array(analyser.frequencyBinCount)
        analyser.getByteFrequencyData(data)
        const avg = data.reduce((a, b) => a + b, 0) / data.length
        setMicActive(avg > 8)
        setRunning(avg > 8)
      }
      micRef.current.interval = setInterval(check, 1500)
    }).catch(() => {})
    return () => {
      if (micRef.current) {
        clearInterval(micRef.current.interval)
        micRef.current.stream?.getTracks().forEach(t => t.stop())
        micRef.current.ctx?.close()
      }
    }
  }, [timerMode])

  // Main timer
  useEffect(() => {
    if (!running) { clearInterval(intervalRef.current); return }
    intervalRef.current = setInterval(() => {
      setSecsLeft(s => {
        if (s <= 1) { clearInterval(intervalRef.current); handleNextSection(); return 0 }
        return s - 1
      })
    }, 1000)
    return () => clearInterval(intervalRef.current)
  }, [running, sectionIdx])

  const handleNextSection = useCallback(() => {
    setLog(prev => {
      const updated = [...prev]
      if (updated[sectionIdx]) {
        updated[sectionIdx] = { ...updated[sectionIdx], minutes: currentSection?.duration_minutes || 8 }
      }
      return updated
    })
    if (sectionIdx < sections.length - 1) {
      setSectionIdx(i => i + 1)
      setSecsLeft((sections[sectionIdx + 1]?.duration_minutes || 8) * 60)
      setRunning(true)
    } else {
      setShowComplete(true)
      setRunning(false)
    }
  }, [sectionIdx, sections, currentSection])

  const handleFinish = async (isPublic) => {
    const totalMinutes = Math.round((Date.now() - startTimeRef.current) / 60000)
    await completeSession(sessionId, totalMinutes, log, notes, isPublic)
    onComplete(totalMinutes)
  }

  const progress = ((totalSecs - secsLeft) / totalSecs) * 100

  if (showComplete) {
    const totalMin = Math.round((Date.now() - startTimeRef.current) / 60000)
    return (
      <div style={{ padding: '32px 20px', display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'center' }}>
        <div style={{ fontSize: 64 }}>🎉</div>
        <div style={{ fontSize: 22, fontWeight: 500, textAlign: 'center' }}>Session ολοκληρώθηκε!</div>
        <div style={{ fontSize: 48, fontWeight: 300, color: 'var(--blue)' }}>{totalMin}λ</div>
        <textarea className="input" placeholder="Σημειώσεις session (προαιρετικό)..."
          value={notes} onChange={e => setNotes(e.target.value)}
          style={{ height: 80, resize: 'none', width: '100%' }} />
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button className="btn-primary" onClick={() => handleFinish(true)}>
            📢 Μοίρασε με την ομάδα
          </button>
          <button className="btn-ghost" style={{ width: '100%', textAlign: 'center' }} onClick={() => handleFinish(false)}>
            🔒 Κράτα το για σένα
          </button>
        </div>
      </div>
    )
  }

  const c = getColor(currentSection?.id)

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--navy)' }}>
      {/* Header */}
      <div style={{ padding: '16px 16px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{program?.title}</div>
      </div>

      {/* Phase indicators */}
      <div style={{ display: 'flex', gap: 6, padding: '0 16px 16px' }}>
        {sections.map((s, i) => {
          const sc = getColor(s.id)
          return <div key={s.id} style={{
            flex: 1, height: 4, borderRadius: 2,
            background: i < sectionIdx ? sc.color : i === sectionIdx ? sc.color : 'var(--border)',
            opacity: i < sectionIdx ? 0.4 : 1,
            transition: 'all 0.3s',
          }} />
        })}
      </div>

      {/* Main timer */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 24px', gap: 16 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Ενότητα {sectionIdx + 1} / {sections.length}</div>

        {/* Circular progress */}
        <div style={{ position: 'relative', width: 180, height: 180 }}>
          <svg width="180" height="180" style={{ position: 'absolute', top: 0, left: 0, transform: 'rotate(-90deg)' }}>
            <circle cx="90" cy="90" r="82" fill="none" stroke="var(--border)" strokeWidth="6" />
            <circle cx="90" cy="90" r="82" fill="none" stroke={c.color} strokeWidth="6"
              strokeDasharray={`${2 * Math.PI * 82}`}
              strokeDashoffset={`${2 * Math.PI * 82 * (1 - progress / 100)}`}
              strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s linear' }} />
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ fontSize: 32 }}>{currentSection?.icon}</div>
            <div style={{ fontSize: 36, fontFamily: 'var(--font-mono)', fontWeight: 500, color: 'var(--text)', letterSpacing: 1 }}>{fmt(secsLeft)}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{currentSection?.name}</div>
          </div>
        </div>

        {/* AI tip */}
        {aiTip ? (
          <div style={{ background: 'var(--navy-light)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px', width: '100%' }}>
            <div style={{ fontSize: 10, color: c.color, fontWeight: 500, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.8 }}>AI Coach</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6 }}>{aiTip}</div>
          </div>
        ) : (
          <div style={{ background: 'var(--navy-light)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px', width: '100%', opacity: 0.5 }}>
            <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>Φορτώνει συμβουλή...</div>
          </div>
        )}

        {/* Mic indicator */}
        {timerMode === 'mic' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--navy-light)', borderRadius: 20, padding: '6px 14px' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: micActive ? 'var(--green)' : 'var(--text-dim)', transition: 'background 0.3s' }} />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {micActive ? '🎤 Ανιχνεύεται ήχος' : '🎤 Δεν ανιχνεύεται — timer σε παύση'}
            </span>
          </div>
        )}

        {/* Controls */}
        <div style={{ display: 'flex', gap: 12, width: '100%', marginTop: 'auto' }}>
          <button onClick={() => setRunning(r => !r)}
            style={{ flex: 1, padding: '14px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--navy-light)', color: 'var(--text)', fontSize: 14, cursor: 'pointer', fontFamily: 'var(--font)' }}>
            {running ? '⏸ Παύση' : '▶ Συνέχεια'}
          </button>
          <button onClick={handleNextSection}
            style={{ flex: 1, padding: '14px', borderRadius: 'var(--radius)', background: c.color, border: 'none', color: 'white', fontSize: 14, cursor: 'pointer', fontFamily: 'var(--font)', fontWeight: 500 }}>
            {sectionIdx < sections.length - 1 ? 'Επόμενο →' : 'Τέλος ✓'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── MAIN SESSION PAGE ─────────────────────────────
export default function Session() {
  const { user } = useAuth()
  const [phase, setPhase] = useState('select') // select | active | done
  const [activeProgram, setActiveProgram] = useState(null)
  const [timerMode, setTimerMode] = useState('simple')
  const [sessionId, setSessionId] = useState(null)
  const [totalMin, setTotalMin] = useState(0)

  const handleStart = async (program, mode, tMode) => {
    const sess = await startSession(user.id, program?.id || null, mode, tMode)
    setSessionId(sess.id)
    setActiveProgram(program)
    setTimerMode(tMode)
    setPhase('active')
  }

  const handleComplete = (min) => {
    setTotalMin(min)
    setPhase('done')
  }

  if (phase === 'active' && activeProgram) {
    return <ActiveSession program={activeProgram} timerMode={timerMode} sessionId={sessionId} onComplete={handleComplete} />
  }

  if (phase === 'done') {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <div style={{ fontSize: 64 }}>🎵</div>
        <div style={{ fontSize: 22, fontWeight: 500 }}>Μπράβο!</div>
        <div style={{ color: 'var(--text-muted)', fontSize: 15 }}>{totalMin} λεπτά μελέτης — we play music!</div>
        <button className="btn-primary" style={{ marginTop: 12, maxWidth: 280 }} onClick={() => setPhase('select')}>
          Νέο session
        </button>
      </div>
    )
  }

  return <ProgramSelector onStart={handleStart} />
}
